import React, { useMemo, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import TableContainer from "components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { getContactListDetail, getContactLists } from "store/contacts-lists/actions";
import { getContactListById, getContactListsById, exportContactsByListId, addNewContacts, updateContact, deleteContact, removeContactFromList, addContactToMultipleLists, updateContactExpiry, apiErrorMessage } from "../../../helpers/api_helper";
import Swal from "sweetalert2";
import Select from "react-select";

const ContactListDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [listName, setListName] = useState("");
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [contactIdToDelete, setContactIdToDelete] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [contactLists, setContactLists] = useState([]);
  const [additionalContactLists, setAdditionalContactLists] = useState([]);

  const { contactLists: availableContactLists } = useSelector(state => state.ContactList);

  const [newContacts, setNewContacts] = useState([
    { firstName: "", lastName: "", mobile: "", email: "" },
  ]);

  const [pagination, setPagination] = useState({
    pageSize: 20,
    page: 0,
    sortBy: 'id',
    ascendingSort: false,
  });
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const triggerReload = () => setRefreshTick((t) => t + 1);

  const toggleModal = () => setModal(!modal);
  const toggleViewModal = () => setViewModal(!viewModal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const toggleEditModal = () => setEditModal(!editModal);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const updatedContacts = [...newContacts];
    updatedContacts[index][name] = value;
    setNewContacts(updatedContacts);
  };

  useEffect(() => {
    if (contactLists && contactLists.length > 0) {
      const existingListIds = contactLists.map(list =>
        String(list.id || list.contact_list_id)
      );
      setAdditionalContactLists(existingListIds);
    }
  }, [contactLists]);

  const handleContactListChange = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setAdditionalContactLists(selectedIds);
  };

  const addContactField = () => {
    setNewContacts([
      ...newContacts,
      { firstName: "", lastName: "", mobile: "", email: "" },
    ]);
  };

  const removeContactField = (index) => {
    const updatedContacts = newContacts.filter((_, i) => i !== index);
    setNewContacts(updatedContacts);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const { ids, failed } = await addNewContacts(id, newContacts);
      if (ids.length) {
        toggleModal();
        setNewContacts([{ firstName: "", lastName: "", mobile: "", email: "" }]);
        triggerReload();
      }
      if (failed.length) {
        const details = failed
          .map((f) => `${f.contact.firstName} ${f.contact.lastName} (${f.contact.mobile}): ${f.message}`)
          .join("; ");
        Swal.fire({
          icon: ids.length ? "warning" : "error",
          title: ids.length ? "Some Contacts Not Added" : "Error",
          text: details,
          confirmButtonText: "OK",
        });
        return;
      }
      Swal.fire({
        icon: "success",
        title: "Contacts Added",
        text: "New contacts have been successfully added.",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error adding contacts:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiErrorMessage(error, "Failed to add contacts. Please try again."),
        confirmButtonText: "OK",
      });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      // Step 1: update the contact itself.
      await updateContact(contactToEdit.id, contactToEdit);

      // Step 2: add the contact to any additionally-selected lists.
      if (additionalContactLists.length > 0) {
        await addContactToMultipleLists({
          contactId: contactToEdit.id,
          listIds: additionalContactLists,
        });
      }

      Swal.fire({
        icon: "success",
        title: "Contact Updated",
        text: "The contact has been successfully updated and added to selected lists.",
        confirmButtonText: "OK",
      });
      toggleEditModal();
      triggerReload();
    } catch (error) {
      console.error("Error updating contact:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update contact or add to lists. Please try again.",
        confirmButtonText: "OK",
      });
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setContactToEdit(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteContact = (contactId) => {
    setContactIdToDelete(contactId);
    toggleDeleteModal();
  };

  const handleConfirmDelete = async () => {
    try {
      // Bonga: remove just this contact from this list.
      await removeContactFromList(id, contactIdToDelete);
      triggerReload();
      toggleDeleteModal();
      Swal.fire({
        icon: 'success',
        title: 'Contact Deleted',
        text: 'The contact was successfully deleted from this list!',
        confirmButtonText: 'OK',
      });
    } catch (error) {
      console.error("Error deleting contact:", error);
      Swal.fire({
        icon: 'error',
        title: 'Deletion Failed',
        text: 'There was an error deleting the contact.',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleDeleteFromAll = async () => {
    try {
      await deleteContact(contactIdToDelete);
      triggerReload();
      toggleDeleteModal();
      Swal.fire({
        icon: 'success',
        title: 'Contact Deleted',
        text: 'The contact was successfully deleted from all address books!',
        confirmButtonText: 'OK',
      });
    } catch (error) {
      console.error("Error deleting contact from all address books:", error);
      Swal.fire({
        icon: 'error',
        title: 'Deletion Failed',
        text: 'There was an error deleting the contact from all address books.',
        confirmButtonText: 'OK',
      });
    }
  };

  // Server-side load: re-runs on page/size change, committed search, or a mutation.
  useEffect(() => {
    dispatch(getContactListDetail({ id, search: activeSearch, page: pagination.page, pageSize: pagination.pageSize }));
  }, [id, pagination, activeSearch, refreshTick, dispatch]);

  useEffect(() => {
    getContactListById(id).then((d) => setListName(d.name)).catch(() => {});
  }, [id]);

  // Load the full list catalog once so the "Add to Contact Lists" picker has
  // every list (it filters client-side). Without this the Select is empty.
  useEffect(() => {
    dispatch(getContactLists({ page: 0, pageSize: 200 }));
  }, [dispatch]);

  const ContactListProperties = createSelector(
    (state) => state.ContactList,
    (contactList) => ({
      error: contactList.error,
      contactDetailList: contactList.contactDetailList,
      total: contactList.contactDetailTotal,
      totalPages: contactList.contactDetailTotalPages,
    })
  );

  const { error, contactDetailList, total, totalPages } = useSelector(ContactListProperties);

  const runMemberSearch = () => { setPagination((p) => ({ ...p, page: 0 })); setActiveSearch(searchInput.trim()); };
  const clearMemberSearch = () => { setSearchInput(""); setActiveSearch(""); setPagination((p) => ({ ...p, page: 0 })); };

  useEffect(() => {
    if (viewModal && selectedContact) {
      const fetchContactLists = async () => {
        try {
          const lists = await getContactListsById(selectedContact.id);
          setContactLists(lists);
        } catch (error) {
          console.error("Failed to fetch contact lists:", error);
          setContactLists([]);
        }
      };
      fetchContactLists();
      setExpiryDate("");
    }
  }, [viewModal, selectedContact]);

  const handleSetExpiry = async () => {
    if (!expiryDate) {
      Swal.fire({
        icon: "warning",
        title: "No Date Selected",
        text: "Please select an expiry date first.",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      await updateContactExpiry(selectedContact.id, expiryDate);
      Swal.fire({
        icon: "success",
        title: "Expiry Updated",
        text: "Contact expiry date has been successfully updated.",
        confirmButtonText: "OK",
      });
      toggleViewModal();
      triggerReload();
    } catch (error) {
      console.error("Error updating expiry date:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update expiry date. Please try again.",
        confirmButtonText: "OK",
      });
    }
  };

  const handleExportContacts = async () => {
    try {
      const response = await exportContactsByListId(id);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `contacts_${listName}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      Swal.fire({
        icon: "success",
        title: "Export Successful",
        text: "The contact list has been successfully exported.",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error exporting contact list:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: "There was an error exporting the contact list.",
        confirmButtonText: "OK",
      });
    }
  };

  const columns = useMemo(
    () => [
      { header: "First Name", accessorKey: "first_name" },
      { header: "Last Name", accessorKey: "last_name" },
      { header: "Mobile", accessorKey: "msisdn" },
      { header: "Email", accessorKey: "email" },
      {
        header: "Actions",
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <span
              className="text-primary"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedContact(cellProps.row.original);
                toggleViewModal();
              }}
            >
              <i className="mdi mdi-eye font-size-18" />
            </span>
            <span
              className="text-success"
              style={{ cursor: "pointer" }}
              onClick={async () => {
                const contact = cellProps.row.original;
                setContactToEdit(contact);
                try {
                  const lists = await getContactListsById(contact.id);
                  setContactLists(lists);
                } catch (error) {
                  console.error("Failed to fetch contact lists:", error);
                  setContactLists([]);
                }
                toggleEditModal();
              }}
            >
              <i className="mdi mdi-pencil font-size-18" />
            </span>
            <span
              className="text-danger"
              style={{ cursor: "pointer" }}
              onClick={() => handleDeleteContact(cellProps.row.original.id)}
            >
              <i className="mdi mdi-delete font-size-18" />
            </span>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const handleAddNewContact = () => {
    toggleModal();
  };

  const onChangePagination = ({ page, limit }, filters, sorter) => {
    setPagination({ ...pagination, page: page ?? pagination.page, pageSize: limit ?? pagination.pageSize });
  };

  const contactListOptions = availableContactLists?.map(list => ({
    value: String(list.id),
    label: list.name,
  })) || [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Contacts" breadcrumbItem="Contact List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Button color="primary" onClick={() => navigate('/contact-list')}>
                      <i className="mdi mdi-arrow-left font-size-16 align-middle me-2"></i> Back
                    </Button>
                    <h2 className="card-title">
                      {listName || "Contact List"}
                      {` (${(total || 0).toLocaleString()} contacts)`}
                    </h2>
                    <Button outline color="primary" onClick={handleExportContacts}>
                      <i className="mdi mdi-download"></i> Export Data
                    </Button>
                  </div>
                  <Row className="mb-3">
                    <Col md={8}>
                      <div className="d-flex">
                        <Input
                          placeholder="Search this list by name, mobile, email…"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && runMemberSearch()}
                        />
                        <Button color="primary" className="ms-2" onClick={runMemberSearch}>Search</Button>
                        {activeSearch && (
                          <Button color="secondary" outline className="ms-2" onClick={clearMemberSearch}>Clear</Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={contactDetailList}
                    isGlobalFilter={false}
                    isPagination={true}
                    paginationData={pagination}
                    isCustomPageSize={true}
                    onChangePagination={onChangePagination}
                    manualPagination={true}
                    pageCount={totalPages}
                    totalRows={total}
                    tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                    theadClass="table-light"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    pagination="pagination"
                    isAddButton={true}
                    buttonName="New Contact"
                    handleUserClick={handleAddNewContact}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for adding a new contact */}
        <Modal isOpen={modal} toggle={toggleModal} centered className="modal-lg">
          <ModalHeader toggle={toggleModal}>Add New Contacts</ModalHeader>
          <Form onSubmit={handleFormSubmit}>
            <ModalBody>
              {newContacts.map((contact, index) => (
                <Row key={index} className="align-items-end mb-3">
                  <Col md="3">
                    <FormGroup>
                      <Label for={`firstName-${index}`}>First Name</Label>
                      <Input
                        type="text"
                        id={`firstName-${index}`}
                        name="firstName"
                        value={contact.firstName}
                        onChange={(e) => handleChange(index, e)}
                        placeholder="First Name"
                        required
                        style={{ width: "100%" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for={`lastName-${index}`}>Last Name</Label>
                      <Input
                        type="text"
                        id={`lastName-${index}`}
                        name="lastName"
                        value={contact.lastName}
                        onChange={(e) => handleChange(index, e)}
                        placeholder="Last Name"
                        required
                        style={{ width: "100%" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for={`mobile-${index}`}>Mobile</Label>
                      <Input
                        type="text"
                        id={`mobile-${index}`}
                        name="mobile"
                        value={contact.mobile}
                        onChange={(e) => handleChange(index, e)}
                        placeholder="Mobile Number"
                        required
                        style={{ width: "100%" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for={`email-${index}`}>Email</Label>
                      <Input
                        type="email"
                        id={`email-${index}`}
                        name="email"
                        value={contact.email}
                        onChange={(e) => handleChange(index, e)}
                        placeholder="Email"
                        style={{ width: "100%" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1" className="d-flex">
                    <Button
                      color="danger"
                      outline
                      type="button"
                      onClick={() => removeContactField(index)}
                      className="ms-2 mt-auto"
                    >
                      <i className="mdi mdi-minus"></i>
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button
                color="primary"
                outline
                type="button"
                onClick={addContactField}
                className="d-block mt-3"
              >
                <i className="mdi mdi-plus"></i> Add More
              </Button>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                Add Contacts
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Modal for viewing a contact */}
        <Modal isOpen={viewModal} toggle={toggleViewModal} centered className="modal-lg">
          <ModalHeader toggle={toggleViewModal}>Contact Details</ModalHeader>
          <ModalBody>
            {selectedContact && (
              <div className="p-3">
                <Row className="mb-3">
                  <Col md="6">
                    <h6 className="text-muted">First Name</h6>
                    <p className="mb-0">{selectedContact.first_name || '-'}</p>
                  </Col>
                  <Col md="6">
                    <h6 className="text-muted">Last Name</h6>
                    <p className="mb-0">{selectedContact.last_name || '-'}</p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md="6">
                    <h6 className="text-muted">Mobile</h6>
                    <p className="mb-0">{selectedContact.msisdn || '-'}</p>
                  </Col>
                  <Col md="6">
                    <h6 className="text-muted">Email</h6>
                    <p className="mb-0">{selectedContact.email || '-'}</p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md="6">
                    <h6 className="text-muted">Expiry Date</h6>
                    <p className="mb-0">{selectedContact.expiry_date || '-'}</p>
                  </Col>
                  <Col md="6">
                    <h6 className="text-muted">County</h6>
                    <p className="mb-0">{selectedContact.county || '-'}</p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md="12">
                    <h6 className="text-muted">Contact Lists</h6>
                    {contactLists.length > 0 ? (
                      <div
                        className="d-flex flex-wrap gap-2"
                        style={{
                          maxHeight: "150px",
                          overflowY: "auto",
                          border: "1px solid #e9ecef",
                          borderRadius: "4px",
                          padding: "10px",
                        }}
                      >
                        {contactLists.map((list, index) => (
                          <span
                            key={index}
                            className="badge bg-primary rounded-pill"
                            style={{ fontSize: "0.8rem", padding: "8px 12px" }}
                          >
                            {list.name || list.contact_list_name || `List ${index + 1}`}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted mb-0">This contact is not in any lists.</p>
                    )}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md="12">
                    <FormGroup>
                      <Label>Set Expiry Date</Label>
                      <Input
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]} 
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleViewModal}>
              Close
            </Button>
            <Button
              color="primary"
              onClick={handleSetExpiry}
              disabled={!expiryDate}
            >
              Set Expiry
            </Button>
          </ModalFooter>
        </Modal>

        {/* Edit Modal */}
        <Modal isOpen={editModal} toggle={toggleEditModal} centered className="modal-lg">
          <ModalHeader toggle={toggleEditModal}>Edit Contact</ModalHeader>
          <Form onSubmit={handleEditSubmit}>
            <ModalBody>
              {contactToEdit && (
                <>
                  <Row className="align-items-end mb-3">
                    <Col md="3">
                      <FormGroup>
                        <Label>First Name</Label>
                        <Input
                          type="text"
                          name="first_name"
                          value={contactToEdit.first_name}
                          onChange={handleEditChange}
                          placeholder="First Name"
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Last Name</Label>
                        <Input
                          type="text"
                          name="last_name"
                          value={contactToEdit.last_name}
                          onChange={handleEditChange}
                          placeholder="Last Name"
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Mobile</Label>
                        <Input
                          type="text"
                          name="msisdn"
                          value={contactToEdit.msisdn}
                          onChange={handleEditChange}
                          placeholder="Mobile Number"
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          name="email"
                          value={contactToEdit.email}
                          onChange={handleEditChange}
                          placeholder="Email"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md="12">
                      <FormGroup>
                        <Label>Currently in Contact Lists:</Label>
                        {contactLists.length > 0 ? (
                          <div
                            className="d-flex flex-wrap gap-2"
                            style={{
                              maxHeight: "150px",
                              overflowY: "auto",
                              border: "1px solid #e9ecef",
                              borderRadius: "4px",
                              padding: "10px",
                            }}
                          >
                            {contactLists.map((list, index) => (
                              <span
                                key={index}
                                className="badge bg-primary rounded-pill"
                                style={{ fontSize: "0.8rem", padding: "8px 12px" }}
                              >
                                {list.name || list.contact_list_name || `List ${index + 1}`}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted mb-0" style={{ fontStyle: "italic", fontSize: "1.1rem" }}>
                            This contact is not in any other lists.
                          </p>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md="12">
                      <FormGroup>
                        <Label>Add to Contact Lists:</Label>
                        <Select
                          isMulti
                          options={contactListOptions}
                          value={contactListOptions.filter(option => additionalContactLists.includes(option.value))}
                          onChange={handleContactListChange}
                          placeholder="Search and select contact lists..."
                          className="basic-multi-select"
                          classNamePrefix="select"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleEditModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                Update Contact
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Modal for confirming delete */}
        <Modal
          isOpen={deleteModal}
          toggle={toggleDeleteModal}
          centered
          style={{ maxWidth: '700px', width: '100%' }}
        >
          <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
          <ModalBody>
            <p>Are you sure you want to delete this contact?</p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={toggleDeleteModal}>
              Cancel
            </Button>
            <Button
              color="warning"
              onClick={handleConfirmDelete}
              className="btn-orange-outline"
            >
              <i className="mdi mdi-delete font-size-18 me-1" /> Delete from This List
            </Button>
            <Button color="danger" onClick={handleDeleteFromAll}>
              <i className="mdi mdi-delete-forever font-size-18 me-1" /> Delete from All Address Books
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default ContactListDetail;